import { Component } from '@theme/component';

/**
 * Mobile-only sticky Add-to-Cart bar.
 *
 * Visibility rules:
 *  - Hidden while the main buy-buttons block is intersecting the viewport.
 *  - Hidden when a scroll-lock is active (cart drawer, search modal, etc.).
 *
 * Form submission, variant sync, and button state are handled by the nested
 * <product-form-component> and <add-to-cart-component> — no duplication.
 *
 * @extends Component
 */
class StickyAtcComponent extends Component {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** @type {MutationObserver | undefined} */
  #scrollLockObserver;

  connectedCallback() {
    super.connectedCallback();

    this.#watchMainAtc();
    this.#watchScrollLock();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#observer?.disconnect();
    this.#scrollLockObserver?.disconnect();
  }

  #watchMainAtc() {
    const mainAtc = document.querySelector('.buy-buttons-block:not([data-sticky-atc])');
    if (!mainAtc) {
      this.dataset.visible = 'true';
      return;
    }

    this.#observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        this.dataset.visible = entry.isIntersecting ? 'false' : 'true';
      },
      { threshold: 0, rootMargin: '0px 0px -40px 0px' }
    );
    this.#observer.observe(mainAtc);
  }

  #watchScrollLock() {
    const sync = () => {
      if (document.documentElement.hasAttribute('scroll-lock')) {
        this.dataset.scrollLocked = 'true';
      } else {
        delete this.dataset.scrollLocked;
      }
    };

    sync();

    this.#scrollLockObserver = new MutationObserver(sync);
    this.#scrollLockObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['scroll-lock'],
    });
  }
}

if (!customElements.get('sticky-atc-component')) {
  customElements.define('sticky-atc-component', StickyAtcComponent);
}
